<template>
  <div class="custom-field-section">
    <initiative-sub-heading icon-class="cube-icon" heading="Custom Fields" />
    <div class="custom-fields text-small">
      <template v-for="field in customFields">
        <div class="custom-field" :key="field.id">
          <div class="field-label ">{{ field.name }}:</div>
          <computed-field-value
            v-if="field.isComputed"
            :value="field.value"
            :error="field.error"
            :is-pill="true"
          />
          <input
            class="text-field text-small"
            :type="inputType[field.type]"
            :placeholder="'Add ' + field.name"
            :value="field.value"
            @keyup="lastKey = $event.keyCode"
            @keyup.enter="onTextFieldSave(field, $event)"
            @keydown.esc.stop
            @keyup.esc="$event.target.blur()"
            @blur="lastKey !== KEY_CODES.ENTER && onTextFieldSave(field, $event)"
            v-else-if="
              field.type === CUSTOM_FIELD_TYPE.TEXT || field.type === CUSTOM_FIELD_TYPE.NUMBER
            "
          />
          <div
            class="position-relative"
            v-click-outside="
              $event => onDatePickerOutsideClick($event, 'date-' + getFieldId(field.id))
            "
            :ref="'date-' + getFieldId(field.id)"
            v-else-if="field.type === CUSTOM_FIELD_TYPE.DATE"
          >
            <div class="date-field-wrapper" tabIndex="0" :id="'date-field-' + getFieldId(field.id)">
              <component
                class="h-100 d-flex align-items-center field-side-spacing pointer"
                icon-alignment="top"
                :show-shadow-on-icon="true"
                @close="onDateChange(field, null)"
                :is="field.value ? 'with-close-icon' : 'div'"
              >
                <div class="badge date-field flex-center" v-if="field.value">
                  {{ formatDate(field.value) }}
                </div>
                <div class="badge placeholder flex-center text-small field-side-spacing" v-else>
                  Select
                </div>
              </component>
            </div>
            <b-popover
              :ref="'datePopover-' + getFieldId(field.id)"
              custom-class="initiative-cf-field-po"
              triggers="click blur"
              :target="'date-field-' + getFieldId(field.id)"
              boundary="viewport"
              placement="bottom"
            >
              <div @click.stop>
                <date-picker
                  class="position-static"
                  :value="field.value"
                  @input="onDateChange(field, $event)"
                />
              </div>
            </b-popover>
          </div>
          <div v-else-if="field.type === CUSTOM_FIELD_TYPE.USER">
            <single-select
              :value="field.value"
              @select="$event => onFieldChange(field, $event ? $event.id : null)"
              :options="users"
              dismiss-icon-alignment="top"
              placeholder="Select"
            >
              <template v-slot:default="item">
                <div class="d-flex user-pill">
                  <user-short :user-id="item.value" length="18"></user-short>
                  <span
                    class="user-text text-truncate text-small px-1"
                    :title="getDisplayName(item.value)"
                    >{{ getDisplayName(item.value) }}</span
                  >
                </div>
              </template>
              <template v-slot:listItem="option">
                <div class="d-flex dropdown-item-container">
                  <user-short class="pl-2" :user-id="option.option.id" length="18"></user-short>
                  <span class="pl-2 user-text text-medium">{{ option.option.displayName }}</span>
                </div>
              </template>
            </single-select>
          </div>
          <b-dropdown
            v-else-if="field.type === CUSTOM_FIELD_TYPE.DROPDOWN"
            variant="link"
            menu-class="menu-shadow scrollable "
            toggle-class="d-flex rounded-pill p-0 text-decoration-none border-0 select-user"
            no-caret
            boundary="viewport"
          >
            <template slot="button-content">
              <component
                :is="field.value ? 'with-close-icon' : 'div'"
                class="border-0 px-0 flex-center"
                icon-alignment="top"
                :show-shadow-on-icon="true"
                @close="onFieldChange(field, null)"
              >
                <div
                  class="badge d-flex align-items-center field-side-spacing"
                  v-if="field.value"
                  :style="{ backgroundColor: getDropdownFieldColor(field) }"
                >
                  {{ getDropdownFieldValue(field) }}
                </div>
                <span
                  class="badge placeholder dd-field-placeholder flex-center text-small field-side-spacing"
                  v-else
                >
                  Select
                </span>
              </component>
            </template>
            <b-dropdown-item
              :active="field.value === option.id"
              v-for="option in field.dropdownOptions"
              :key="option.id"
              @click="onFieldChange(field, option.id)"
            >
              <cf-dropdown-option
                :option="option"
                :hide-color="shouldHideOptionColor(field.dropdownOptions)"
              />
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
      <icon-dropdown
        icon-title="Add Custom Field"
        :options="remainingCustomFields"
        @change="onNewCustomFieldSelection"
        icon-class="plus-icon"
      />
    </div>
    <hidden-custom-fields-view
      class="ml-4 read-only-cfs"
      :initiative="initiative"
      :custom-fields="hiddenCustomFields"
    />
    <custom-fields-library-modal
      :org-id="initiative.organizationId"
      :project-id="initiative.projectId"
      v-model="showCustomLibrary"
      :team-id="initiative.teamId"
      @added="onCustomFieldAttachment"
      v-if="showCustomLibrary"
    />
  </div>
</template>

<script>
import DatePicker from '@/components/form/DatePicker.vue'
import SingleSelect from '@/components/form/SingleSelect.vue'
import IconDropdown from '@/components/IconDropdown.vue'
import CfDropdownOption from '@/components/initiative/CfDropdownOption.vue'
import ComputedFieldValue from '@/components/initiative/ComputedFieldValue.vue'
import CustomFieldsLibraryModal from '@/components/initiative/CustomFieldsLibraryModal.vue'
import HiddenCustomFieldsView from '@/components/initiative/HiddenCustomFieldsView.vue'
import InitiativeSubHeading from '@/components/initiative/InitiativeSubHeading.vue'
import UserShort from '@/components/UserShort.vue'
import WithCloseIcon from '@/components/WithCloseIcon.vue'
import { NO_COLOR } from '@/constants/common'
import { CUSTOM_FIELD_TYPE, CUSTOM_FIELD_TYPE_KEY } from '@/constants/customFields'
import events from '@/constants/events'
import { KEY_CODES } from '@/constants/keyCodes'
import { DEFAULT_ERROR } from '@/constants/messages'
import { TOAST_TYPE } from '@/constants/toasts'
import EventBus from '@/EventBus'
import ComponentIdMixin from '@/mixins/ComponentIdMixin'
import EventBusMixin from '@/mixins/EventBusMixin'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import { formatDate } from '@/utils/formatDate'
import getHexColorValueFromCode from '@/utils/initiative/getHexColorValueFromCode'
import sleep from '@/utils/sleep'
import { BButton, BDropdown, BDropdownItem, BPopover } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'

export default {
  name: 'CustomFieldsView',
  props: ['initiative'],
  components: {
    ComputedFieldValue,
    CustomFieldsLibraryModal,
    HiddenCustomFieldsView,
    DatePicker,
    BDropdown,
    BDropdownItem,
    UserShort,
    SingleSelect,
    IconDropdown,
    BPopover,
    BButton,
    WithCloseIcon,
    InitiativeSubHeading,
    CfDropdownOption
  },
  data() {
    return {
      CUSTOM_FIELD_TYPE,
      showDateSelector: {},
      lastKey: null,
      showCustomLibrary: false,
      KEY_CODES,
      inputType: {
        [CUSTOM_FIELD_TYPE.NUMBER]: 'number',
        [CUSTOM_FIELD_TYPE.TEXT]: 'text'
      },
      searchText: '',
      customFields: [],
      hiddenCustomFields: []
    }
  },
  mixins: [MembershipsMixin, EventBusMixin, ComponentIdMixin],
  computed: {
    displayFields() {
      let config
      if (this.initiative.projectId) {
        config = this.$store.getters.initiativeDisplayFieldsByProjectId(this.initiative.projectId)
      } else {
        config = this.$store.getters.initiativeDisplayFieldsByTeamId(this.initiative.teamId)
      }
      return config?.customFieldDefinitionIds
    },
    enabledCustomFields() {
      if (this.initiative.projectId) {
        return this.$store.getters.initiativeEnabledCustomFieldsByProjectId(
          this.initiative.projectId
        )
      } else {
        return this.$store.getters.initiativeEnabledCustomFieldsByTeamId(this.initiative.teamId)
      }
    },
    orgCustomFields() {
      return this.$store.state.orgCustomFields[this.orgId] || []
    },
    user() {
      return this.$store.state.user
    },
    memberships() {
      const members = this.orgId ? this.$store.getters.orgMemberships(this.orgId) : []
      return R.sort(({ user: a }, { user: b }) => {
        return a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase())
      }, members)
    },
    users() {
      return this.memberships.map(({ user }) => ({ ...user, label: user.displayName }))
    },
    orgId() {
      return this.initiative.organizationId
    },
    remainingCustomFields() {
      const fields = R.differenceWith(
        (f1, f2) => f1.id === f2.id,
        this.enabledCustomFields || [],
        this.customFields
      ).map(field => ({
        value: field.id,
        label: field.name,
        field
      }))
      const useExistingPlaceholder = {
        value: 'use-existing',
        label: '+ Use from library',
        field: 'use-existing'
      }
      return [...fields, useExistingPlaceholder]
    }
  },
  methods: {
    ...mapActions([
      'getDisplayInitiativeFields',
      'getInitiativeCustomFields',
      'getEnabledCustomFields'
    ]),
    shouldHideOptionColor(dropdownOptions) {
      return dropdownOptions.every(option => option.color === NO_COLOR)
    },
    getDisplayName(userId) {
      const user = this.$store.getters.userById(userId) || {}
      return user.displayName
    },
    onDatePickerOutsideClick(event, fieldRef) {
      if (!this.$refs[fieldRef].includes(event.target)) {
        this.toggleDateField(fieldRef, false)
      }
    },
    formatDate,
    getFieldId(fieldId) {
      return this.uuid + '-' + fieldId
    },
    onDateChange(field, value) {
      this.toggleDateField('date-' + this.getFieldId(field.id), false)
      this.onFieldChange(field, value)
      this.$refs['datePopover-' + this.getFieldId(field.id)][0].$emit('close')
    },
    onTextFieldSave(field, event) {
      let value = event.target.value
      if (event.keyCode === KEY_CODES.ENTER) {
        event.target.blur()
      }
      if (field.type === CUSTOM_FIELD_TYPE.NUMBER) {
        value = value ? +value : null
      }
      this.onFieldChange(field, value)
    },
    getAPIErrorMessage(response) {
      const numberFieldError = R.path(['detail', 'numberValue', '0'], response)
      const userFieldError = R.path(['detail', 'userId', '0'], response)
      const dropdownFieldError = R.path(['detail', 'dropdownOptionId', '0'], response)
      const textFieldError = R.path(['detail', 'textValue', '0'], response)
      const dateFieldError = R.path(['detail', 'dateValue', '0'], response)
      const commonMessage = R.path(['message'], response)
      return (
        numberFieldError ||
        userFieldError ||
        dropdownFieldError ||
        textFieldError ||
        dateFieldError ||
        commonMessage ||
        DEFAULT_ERROR
      )
    },
    updateCustomFieldValue(field, value) {
      const fieldIndex = this.customFields.findIndex(x => x.id === field.id)
      this.customFields = R.assocPath([fieldIndex, 'value'], value, this.customFields)
    },
    async onFieldChange(field, value) {
      if (value !== field.value) {
        try {
          await this.$store.dispatch('setInitiativeCustomField', {
            orgId: this.orgId,
            initiativeId: this.initiative.id,
            field: field,
            value: value
          })
          this.updateCustomFieldValue(field, value)
          if (field.type === CUSTOM_FIELD_TYPE.NUMBER) {
            // adding a sleep to make sure initiative re-fetch happens after its computed fields are updated.
            await sleep(1000)
          }
          this.eventBusEmit(events.INITIATIVE_CUSTOM_FIELD_VALUE_UPDATED, {
            initiativeId: this.initiative.id,
            fieldId: field.id,
            value
          })
        } catch (e) {
          const message = this.getAPIErrorMessage(e.response.data)
          EventBus.$emit('ADD_TOAST', { message, type: TOAST_TYPE.ERROR })
        }
      }
    },
    toggleDateField(fieldId, show) {
      const newState = typeof show === 'undefined' ? !this.showDateSelector[fieldId] : show
      this.showDateSelector = { ...this.showDateSelector, [fieldId]: newState }
    },
    getCustomFieldValue(field, definition) {
      if (!field) return null
      const valueKey = CUSTOM_FIELD_TYPE_KEY[field.type]
      return definition[valueKey]
    },
    getDropdownFieldValue(field) {
      const dropdownOption = field.dropdownOptions.find(R.propEq('id', field.value)) || {}
      return dropdownOption.name
    },
    getHexColorValueFromCode,
    getDropdownFieldColor(field) {
      const dropdownOption = field.dropdownOptions.find(R.propEq('id', field.value)) || {}
      return dropdownOption.color !== NO_COLOR
        ? getHexColorValueFromCode(dropdownOption.color)
        : null
    },
    isCFUpdated(fieldId) {
      const field = this.customFields.find(x => x.id === fieldId)
      return field?.__updated
    },
    async populateCustomFields(forceFetch) {
      let customFieldValues = this.initiative.customFields
      if (!customFieldValues || forceFetch) {
        customFieldValues = await this.fetchCustomFieldValues()
      }
      const displayFieldsDefinition = (this.displayFields || []).map(fieldId =>
        this.orgCustomFields.find(f => f.id === fieldId)
      )
      const otherFieldsDefinition = customFieldValues
        .filter(definition => {
          const displayField = (this.displayFields || []).find(
            fieldId => fieldId === definition.definitionId
          )
          const field = this.orgCustomFields.find(field => field.id === definition.definitionId)
          const value = this.getCustomFieldValue(field, definition)
          return !displayField && (value || this.isCFUpdated(field.id) || definition.error)
        })
        .map(definition => this.orgCustomFields.find(f => f.id === definition.definitionId))
      let fieldsDefinition = [...displayFieldsDefinition, ...otherFieldsDefinition]
      customFieldValues.forEach(definition => {
        const fieldIndex = fieldsDefinition.findIndex(field => field.id === definition.definitionId)
        if (fieldIndex > -1) {
          const value = this.getCustomFieldValue(fieldsDefinition[fieldIndex], definition)
          fieldsDefinition[fieldIndex] = {
            ...fieldsDefinition[fieldIndex],
            value,
            error: definition.error
          }
        }
      })
      let [visibleCustomFields, hiddenCustomFields] = R.partition(
        field => this.enabledCustomFields?.find(cf => cf.id === field.id),
        fieldsDefinition
      )
      const sortByIsComputedAndId = R.sortWith([
        R.ascend(R.prop('isComputed')),
        R.ascend(R.prop('id'))
      ])
      this.customFields = sortByIsComputedAndId(visibleCustomFields)
      this.hiddenCustomFields = hiddenCustomFields
    },
    fetchCustomFieldValues() {
      return this.getInitiativeCustomFields({
        orgId: this.initiative.organizationId,
        initiativeId: this.initiative.id
      })
    },
    async onInitiativeCustomFieldUpdate({ fieldId, value, initiativeId }) {
      const fieldIndex = this.customFields.findIndex(x => x.id === fieldId)
      if (fieldIndex > -1) {
        let field = this.customFields[fieldIndex]
        field = { ...field, value, __updated: true }
        this.customFields = R.assocPath([fieldIndex], field, this.customFields)
      } else if (initiativeId === this.initiative.id && value) {
        this.populateCustomFields(true)
      }
    },
    onNewCustomFieldSelection({ field }) {
      if (field === 'use-existing') {
        this.showCustomLibrary = true
      } else {
        this.customFields.push(field)
      }
    },
    /**
     * fetches custom fields that are displayed in table view, and custom field values
     * @returns {Promise<void>}
     */
    async fetchCustomFieldDetails(forceFetch) {
      const fetchPromises = []
      if (forceFetch || !this.enabledCustomFields) {
        fetchPromises.push(
          this.getEnabledCustomFields({
            orgId: this.initiative.organizationId,
            teamId: this.initiative.teamId,
            projectId: this.initiative.projectId
          })
        )
      }
      if (forceFetch || !this.displayFields) {
        const { teamId, organizationId, projectId } = this.initiative
        fetchPromises.push(
          this.getDisplayInitiativeFields({ orgId: organizationId, teamId, projectId })
        )
      }
      await Promise.allSettled(fetchPromises)
      this.populateCustomFields()
    },
    async onCustomFieldAttachment(cfIds) {
      await this.fetchCustomFieldDetails(true)
      const cfDefinitions = cfIds.map(this.$store.getters.customFieldDefinitionById)
      this.customFields = [...this.customFields, ...cfDefinitions]
    }
  },
  async mounted() {
    if (R.isEmpty(this.memberships) && this.orgId) {
      this.updateOrgMemberships(this.orgId)
    }
    this.fetchCustomFieldDetails()
    this.listenToEventBus(
      events.INITIATIVE_CUSTOM_FIELD_VALUE_UPDATED,
      this.onInitiativeCustomFieldUpdate
    )
  },
  watch: {
    initiative() {
      this.populateCustomFields()
    },
    orgCustomFields(current, previous) {
      if (previous?.length > 0 && current?.length < previous.length) {
        // custom field is deleted
        this.populateCustomFields(true)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';
@import '~@/styles/components/badge';

$close-icon-transition: opacity ease-out 200ms;
$placeholder-color: $secondary;
$field-side-spacing: 10px;

.custom-field-section {
  margin: 0 28px 16px 28px;
  border-bottom: 1px solid $borderPrimary;
  padding-bottom: 16px;
}

.text-field {
  border-radius: 20px;
  padding: 1px $field-side-spacing;
  border: 1px solid $borderPrimary;
  background-color: $quaternary;
  width: 100px;
  transition: background-color ease-in 200ms;
  height: 25px;

  &:focus {
    outline: none;
  }
}

.custom-field {
  display: flex;
  align-items: center;
  margin-bottom: 8px;

  &:not(:last-child) {
    margin-right: 16px;
  }
}

.field-label {
  margin-right: 4px;
}

.custom-fields {
  display: flex;
  flex-wrap: wrap;
  margin-left: 24px;
}

.date-field-wrapper:focus {
  outline: none;
}

.badge {
  background-color: $quaternary;
  padding: 0 $field-side-spacing;
  color: $prime;
  font-weight: normal;
  height: 25px;
  transition: all ease-in 200ms;
  border: 1px solid transparent;
}

.field-side-spacing {
  padding-left: $field-side-spacing;
  padding-right: $field-side-spacing;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}

/deep/ .dropdown-field {
  padding: 1px $field-side-spacing;
  background-color: $quaternary;
}

.dd-field-placeholder {
  padding-top: 1px;
}

.edit-state {
  background-color: $editStateColor;
  box-shadow: inset 0 0 3px rgba(0, 0, 0, 0.1);
}

.text-field:focus,
.select-user[aria-expanded='true'] .badge,
.date-field-wrapper[aria-describedby] .badge {
  @extend .edit-state;
}

.placeholder {
  color: $placeholder-color;
}

.read-only-cfs {
  padding-top: 14px;
  margin-top: 8px;
  border-top: 1px solid $borderPrimary;
}
/* Generalize below implementation of single-select if there are multiple such usage going forward */

/deep/ .single-select .field {
  $field-height: 25px;
  height: $field-height;
  padding: 0 10px 0 5px;
  border: none;
  background-color: $quaternary;

  .chip {
    margin: 0;
    padding: 0;
  }

  input {
    padding: 0 10px;
    margin: 0px -10px 0 -6px;
    flex-grow: 1;
    font-size: 0.75rem;
    height: $field-height;
    border-radius: 20px;

    &:focus {
      @extend .edit-state;
      border: 1px solid $borderPrimary;
    }

    &::placeholder {
      color: $placeholder-color;
    }
  }

  .remove {
    background-color: $documentColor;
    height: 16px;
    width: 16px;
    opacity: 0;
    transition: $close-icon-transition;
  }

  &:hover .remove {
    opacity: 1;
  }
}
</style>
